/**
 * PlanningResultsPage - Router/wrapper for scenario results.
 *
 * Route: /planning/:id/results
 *
 * Fetches the real scenario from the backend via useScenario(id),
 * determines analysis type, and renders the appropriate results page:
 *   - optimization -> OptimizationInlineResults (direct optimizer call with what-if)
 *   - cba -> CBAResultsPage
 *   - long_term -> LongTermResultsPage
 *
 * Passes community currency and communityId to child pages.
 */

import React, { useMemo, useEffect, useState, useRef } from 'react'
import { useParams, useSearchParams, useLocation, Link } from 'react-router-dom'
import { Container, Row, Col, Card, Alert, Button, Spinner } from 'react-bootstrap'
import Chart from 'react-apexcharts'
import CBAResultsPage from './CBAResultsPage'
import LongTermResultsPage from './LongTermResultsPage'
import BeforeAfterKPI from '../components/charts/BeforeAfterKPI'
import EnergyBalanceChart from '../components/charts/EnergyBalanceChart'
import { useScenario } from '../hooks/useScenarios'
import { useCommunity } from '../hooks/useCommunities'
import { useCommunityAnnualLoad } from '../hooks/useDatasets'
import { buildOptimizerRequest } from '../utils/buildOptimizerRequest'
import { resultsAPI } from '../api/results'
import client from '../api/client'
import { useTranslation, Trans } from 'react-i18next'

/**
 * Inline Day-Ahead Optimization results.
 *
 * Builds the optimizer request the same way the dashboard does
 * (loading real building data, datasets, forecasts, tariffs), then
 * applies what-if parameters on top and calls the optimizer directly.
 * Results are saved to the DB for persistence.
 */
function OptimizationInlineResults({ scenarioId, community, scenario }) {
  const { t } = useTranslation()
  const [isRunning, setIsRunning] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)
  const [saved, setSaved] = useState(false)
  const [warnings, setWarnings] = useState([])
  const [balanceScope, setBalanceScope] = useState('community')
  const hasTriggered = useRef(false)

  const communityId = community?.id
  const currency = community?.currency || 'EUR'
  const FX_RATES = { CZK: 25, EUR: 1, PLN: 4.3, HUF: 395, RON: 5, BGN: 1.96 }
  const fxRate = FX_RATES[currency] || 1

  // Build real buildings list for the energy balance dropdown
  const buildings = community?.buildings || []
  const buildingDropdown = useMemo(() => {
    const items = [{ id: 'community', label: 'Community (Total)' }]
    for (const b of buildings) {
      const bid = b.building_id || b.id
      items.push({ id: bid, label: bid })
    }
    return items
  }, [buildings])

  // Extract what-if params from scenario metadata
  const meta = scenario?.metadata || {}
  const optParams = meta.optimizationParams || {}
  const objective = optParams.objective || scenario?.objective || 'min_cost'

  // Run optimization once when community data is available
  useEffect(() => {
    if (!community || !communityId || hasTriggered.current) return
    hasTriggered.current = true

    const runOptimization = async () => {
      setIsRunning(true)
      setError(null)

      try {
        // Build the what-if params from the optimization form
        const whatIfParams = {}
        if (optParams.loadGrowthPct) whatIfParams.loadGrowthPct = optParams.loadGrowthPct
        if (optParams.tariffChangePct) whatIfParams.tariffChangePct = optParams.tariffChangePct
        if (optParams.addPvSystem) {
          whatIfParams.addPvSystem = true
          whatIfParams.newPvCapacityKwp = optParams.newPvCapacityKwp || 0
        }
        if (optParams.addWindTurbine) {
          whatIfParams.addWindTurbine = true
          whatIfParams.newWindCapacityKw = optParams.newWindCapacityKw || 0
        }
        if (optParams.addBattery) {
          whatIfParams.addBattery = true
          whatIfParams.newBatteryCapacityKwh = optParams.newBatteryCapacityKwh || 0
          whatIfParams.newBatteryChargeRateKw = optParams.newBatteryChargeRateKw || 0
          whatIfParams.newBatteryDischargeRateKw = optParams.newBatteryDischargeRateKw || 0
          whatIfParams.newBatteryEfficiencyPct = optParams.newBatteryEfficiencyPct || 90
        }

        const hasWhatIf = Object.keys(whatIfParams).length > 0

        // Build the full optimizer request (same logic as dashboard ModellingPage)
        const optimizerRequest = await buildOptimizerRequest({
          community,
          communityId,
          objective,
          whatIfParams: hasWhatIf ? whatIfParams : null,
        })

        // Call the optimizer directly
        const response = await client.post('/v1/model/optimize', optimizerRequest, {
          timeout: 120000,
        })

        if (response.status === 'validation_error') {
          setError(`Validation error: ${(response.validation_errors || []).join(', ')}`)
          return
        }

        // Collect any warnings
        const allWarnings = []
        if (response.flexLoadWarnings) {
          allWarnings.push(...response.flexLoadWarnings)
        }

        setResult(response)
        setWarnings(allWarnings)
      } catch (err) {
        console.error('Optimization failed:', err)
        setError(err?.response?.data?.detail || err.message || 'Optimization failed')
      } finally {
        setIsRunning(false)
      }
    }

    runOptimization()
  }, [community, communityId])

  // Extract display data from result
  const baselineKPIs = result?.baselineKPIs || result?.baseline_kpis || null
  const optimizedKPIs = result?.optimizedKPIs || result?.optimized_kpis || result?.kpis || null
  const communityEnergyBalance = result?.energyBalance || result?.energy_balance || null
  const batterySchedule = result?.batterySchedule || result?.battery_schedule || null

  // Build per-building energy balance map from schedules
  const perBuildingBalances = useMemo(() => {
    const perBuilding = result?.schedules?.per_building
    if (!perBuilding || !Array.isArray(perBuilding)) return {}
    const map = {}
    for (const bs of perBuilding) {
      const bid = bs.building_id
      if (!bid) continue
      const hours = Array.from({ length: 24 }, (_, i) => i)
      map[bid] = {
        hours,
        load: bs.load_kwh || hours.map(() => 0),
        pv: bs.pv_used_kw || hours.map(() => 0),
        flexLoad: bs.flex_load_kw || hours.map(() => 0),
        wind: hours.map(() => 0),
        gridImport: bs.grid_import_kw || hours.map(() => 0),
        gridExport: bs.grid_export_kw || hours.map(() => 0),
        batteryCharge: hours.map(() => 0),
        batteryDischarge: hours.map(() => 0),
        commAllocation: bs.comm_allocation_kw || hours.map(() => 0),
      }
    }
    return map
  }, [result])

  // Select energy balance based on scope
  const energyBalance = balanceScope === 'community'
    ? communityEnergyBalance
    : perBuildingBalances[balanceScope] || null

  if (isRunning) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" style={{ color: 'var(--color-primary)' }} />
        <p className="mt-3" style={{ color: 'var(--color-neutral-600)' }}>
          {t('planning_results.running_title')}
        </p>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-500)' }}>
          {t('planning_results.running_subtext')}
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="danger" className="mt-4">
        <strong>{t('planning_results.error_failed')}</strong> {error}
      </Alert>
    )
  }

  if (!optimizedKPIs) {
    return (
      <Alert variant="info" className="mt-4">
        <strong>{t('planning_results.no_results_title')}</strong> {t('planning_results.no_results_text')}
      </Alert>
    )
  }

  const hours = Array.from({ length: 24 }, (_, i) => i)
  const batterySoC = batterySchedule?.soc || hours.map(() => 50)
  const batteryCharge = batterySchedule?.charge || hours.map(() => 0)
  const batteryDischarge = batterySchedule?.discharge || hours.map(() => 0)

  const batteryOptions = {
    chart: { type: 'line', height: 320, toolbar: { show: true } },
    colors: ['#8CB808', '#27AE60', '#5C5C5C'],
    stroke: { curve: 'smooth', width: [3, 2, 2] },
    fill: { opacity: [1, 0.3, 0.3] },
    dataLabels: { enabled: false },
    legend: { position: 'top', horizontalAlign: 'left', fontSize: '12px' },
    xaxis: { categories: hours, title: { text: t('results.hour_of_day') }, labels: { style: { fontSize: '11px' } } },
    yaxis: [
      { title: { text: t('results.soc_pct') }, min: 0, max: 100, labels: { formatter: v => v != null ? v.toFixed(0) : '' } },
      { opposite: true, title: { text: t('results.power_kw') }, labels: { formatter: v => v != null ? v.toFixed(1) : '' } },
    ],
    tooltip: { shared: true, intersect: false, y: { formatter: v => v != null ? v.toFixed(2) : '' } },
    grid: { borderColor: '#e0e0e0', strokeDashArray: 3 },
  }
  const batterySeries = [
    { name: t('results.soc_pct'), type: 'line', data: batterySoC },
    { name: t('results.charge_kw'), type: 'area', data: batteryCharge },
    { name: t('results.discharge_kw'), type: 'area', data: batteryDischarge },
  ]

  // What-if summary for display
  const whatIfSummary = []
  if (optParams.loadGrowthPct) whatIfSummary.push(t('planning_results.whatif_load', { value: `${optParams.loadGrowthPct > 0 ? '+' : ''}${optParams.loadGrowthPct}%` }))
  if (optParams.tariffChangePct) whatIfSummary.push(t('planning_results.whatif_tariff', { value: `${optParams.tariffChangePct > 0 ? '+' : ''}${optParams.tariffChangePct}%` }))
  if (optParams.addPvSystem) whatIfSummary.push(t('planning_results.whatif_pv', { value: optParams.newPvCapacityKwp || 0 }))
  if (optParams.addWindTurbine) whatIfSummary.push(t('planning_results.whatif_wind', { value: optParams.newWindCapacityKw || 0 }))
  if (optParams.addBattery) whatIfSummary.push(t('planning_results.whatif_battery', { value: optParams.newBatteryCapacityKwh || 0 }))

  return (
    <div>
      <div className="tool-page-header mb-4">
        <h2 className="tool-page-title">{t('planning_results.header_title')}</h2>
        <p className="tool-page-subtitle">
          {t('planning_results.header_subtitle')}
          {whatIfSummary.length > 0 && (
            <span style={{ marginLeft: 8, color: 'var(--color-primary)' }}>
              ({whatIfSummary.join(' | ')})
            </span>
          )}
        </p>
      </div>

      {warnings.length > 0 && (
        <Alert variant="warning" className="mb-4">
          <strong>{t('planning_results.warnings_label')}</strong>
          <ul className="mb-0 mt-1">
            {warnings.map((w, i) => (
              <li key={i}>{typeof w === 'string' ? w : w.message || JSON.stringify(w)}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
        <Card.Body>
          <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>{t('results.optimization_impact')}</h6>
          <BeforeAfterKPI baselineKPIs={baselineKPIs} optimizedKPIs={optimizedKPIs} currency={currency} />
        </Card.Body>
      </Card>

      <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
        <Card.Body>
          <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>{t('results.energy_balance')}</h6>
          <EnergyBalanceChart
            data={energyBalance}
            scope={balanceScope}
            onScopeChange={setBalanceScope}
            buildings={buildingDropdown}
          />
        </Card.Body>
      </Card>

      <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
        <Card.Body>
          <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>{t('results.battery_schedule')}</h6>
          <Chart options={batteryOptions} series={batterySeries} type="line" height={320} />
        </Card.Body>
      </Card>

      <Row className="g-3 mb-4">
        <Col sm={6} lg={3}>
          <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('results.daily_cost')}</div>
              <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-success)' }}>
                {optimizedKPIs.total_cost_eur != null
                  ? `${(optimizedKPIs.total_cost_eur * fxRate).toFixed(2)} ${currency}`
                  : optimizedKPIs.daily_cost_eur != null
                    ? `${(optimizedKPIs.daily_cost_eur * fxRate).toFixed(2)} ${currency}`
                    : '—'}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} lg={3}>
          <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('results.self_sufficiency')}</div>
              <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-primary)' }}>
                {optimizedKPIs.self_sufficiency_pct != null ? `${optimizedKPIs.self_sufficiency_pct.toFixed(1)}%` : '—'}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} lg={3}>
          <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('results.grid_import')}</div>
              <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>
                {optimizedKPIs.total_grid_import_kwh != null
                  ? `${optimizedKPIs.total_grid_import_kwh.toFixed(1)} kWh`
                  : optimizedKPIs.grid_import_kwh != null
                    ? `${optimizedKPIs.grid_import_kwh.toFixed(0)} kWh`
                    : '—'}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} lg={3}>
          <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('results.co2_avoided')}</div>
              <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-primary)' }}>
                {optimizedKPIs.co2_avoided_kg != null ? `${optimizedKPIs.co2_avoided_kg.toFixed(1)} kg` : '—'}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="d-flex gap-2 justify-content-end mb-4">
        {saved ? (
          <Button variant="outline-success" disabled>
            {t('planning_results.results_saved')}
          </Button>
        ) : (
          <Button
            style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
            onClick={async () => {
              if (!communityId || !result) return
              try {
                const tomorrow = new Date()
                tomorrow.setDate(tomorrow.getDate() + 1)
                const dateStr = tomorrow.toISOString().split('T')[0]
                await resultsAPI.save(communityId, {
                  result_type: 'optimization',
                  name: `What-If Optimization (${objective}) - ${dateStr}`,
                  parameters: {
                    objective,
                    date: dateStr,
                    scenario_id: scenarioId,
                    what_if: optParams,
                  },
                  result_data: {
                    baselineKPIs: result.baselineKPIs || result.baseline_kpis,
                    optimizedKPIs: result.optimizedKPIs || result.optimized_kpis || result.kpis,
                    energyBalance: result.energyBalance || result.energy_balance,
                    batterySchedule: result.batterySchedule || result.battery_schedule,
                    kpis: result.kpis,
                    flexLoads: result.flexLoads,
                    flexLoadWarnings: result.flexLoadWarnings,
                    schedules: result.schedules,
                    solver_status: result.solver_status,
                  },
                })
                setSaved(true)
              } catch (err) {
                console.error('Failed to save results:', err)
              }
            }}
          >
            {t('results.save_results')}
          </Button>
        )}
      </div>
    </div>
  )
}

function PlanningResultsPage() {
  const { t } = useTranslation()
  const { id: scenarioId } = useParams()
  const [searchParams] = useSearchParams()
  const location = useLocation()

  // Router state from PlanningSetupPage (contains form data when navigating from wizard)
  const routerState = location.state || {}

  // Fetch real scenario from backend
  const { data: scenario, isLoading: scenarioLoading, error: scenarioError } = useScenario(scenarioId)

  // Get analysis type from URL query param or scenario record
  const analysisType = useMemo(() => {
    const typeFromQuery = searchParams.get('type')
    if (typeFromQuery) return typeFromQuery
    if (scenario?.scope === 'day_ahead') return 'optimization'
    return scenario?.analysis_type || scenario?.metadata?.analysis_type || 'cba'
  }, [scenario, searchParams])

  // Fetch community data for currency and other metadata
  const communityId = scenario?.community_id || routerState.communityId || null
  const { data: community } = useCommunity(communityId)
  const { data: computedAnnualLoad } = useCommunityAnnualLoad(communityId)
  const currency = routerState.currency || community?.currency || scenario?.finance_assumptions?.currency || 'EUR'

  // Build params: prefer router state (from wizard), fall back to scenario DB record
  const scenarioParams = useMemo(() => {
    // If we have form params passed via router state, use those directly
    const stateParams = analysisType === 'cba' ? routerState.cbaParams
      : analysisType === 'long_term' ? routerState.longTermParams
      : routerState.optimizationParams

    if (stateParams) {
      return {
        ...stateParams,
        // Use computed load from datasets as fallback if form didn't set it
        annualLoadKwh: stateParams.annualLoadKwh || computedAnnualLoad || 65000,
        currency,
        country: community?.metadata?.country || routerState.country || 'Czech Republic',
        scenarioName: routerState.formData?.name || scenario?.name || 'Scenario',
      }
    }

    // Otherwise build from scenario DB record
    if (!scenario) return {}

    // For CBA scenarios, the full params are stored in metadata.cbaParams
    // Pass them through directly so CBAResultsPage uses the exact same inputs
    const meta = scenario.metadata || {}
    if (analysisType === 'cba' && meta.cbaParams) {
      return {
        ...meta.cbaParams,
        annualLoadKwh: meta.cbaParams.annualLoadKwh || computedAnnualLoad || 65000,
        country: community?.metadata?.country || 'Czech Republic',
        currency,
        scenarioName: scenario.name,
      }
    }

    // For long_term scenarios, pass through longTermParams if stored
    if (analysisType === 'long_term' && meta.longTermParams) {
      return {
        ...meta.longTermParams,
        annualLoadKwh: meta.longTermParams.annualLoadKwh || computedAnnualLoad || 65000,
        country: community?.metadata?.country || 'Czech Republic',
        currency,
        scenarioName: scenario.name,
      }
    }

    // Generic fallback for optimization or legacy scenarios
    const fa = scenario.finance_assumptions || {}
    const policies = scenario.policies || {}
    return {
      analysisPeriodYears: fa.analysis_period_years || meta.analysis_period_years || 20,
      discountRate: fa.discount_rate != null ? fa.discount_rate * 100 : 8,
      pvSizeKwp: policies.pv_capacity_kwp || meta.pv_size_kwp || 42,
      battCapacityKwh: policies.battery_capacity_kwh || meta.batt_capacity_kwh || 0,
      windCapacityKwp: policies.wind_capacity_kwp || 0,
      annualLoadKwh: meta.annual_load_kwh || computedAnnualLoad || 65000,
      timeHorizon: meta.time_horizon || '5_years',
      tariffImportEur: fa.tariff_import_eur_kwh || null,
      tariffExportEur: fa.tariff_export_eur_kwh || null,
      pvUnitCostEur: fa.pv_unit_cost_eur || null,
      battUnitCostEur: fa.batt_unit_cost_eur || null,
      co2Factor: fa.co2_factor_kg_kwh || null,
      country: community?.metadata?.country || 'Czech Republic',
      currency,
      scenarioName: scenario.name,
    }
  }, [scenario, currency, routerState, analysisType, community, computedAnnualLoad])

  if (scenarioLoading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" style={{ color: 'var(--color-primary)' }} />
        <p className="mt-3" style={{ color: 'var(--color-neutral-600)' }}>
          {t('planning_results.loading_scenario')}
        </p>
      </Container>
    )
  }

  // If scenario not found AND no router state params, show error
  const hasRouterParams = !!(routerState.cbaParams || routerState.longTermParams || routerState.optimizationParams)
  if ((scenarioError || (!scenarioLoading && !scenario)) && !hasRouterParams) {
    return (
      <Container style={{ maxWidth: '1400px' }}>
        <div className="mb-4">
          <Link
            to="/planning"
            style={{ color: 'var(--color-neutral-600)', textDecoration: 'none', fontSize: 'var(--font-size-sm)' }}
          >
            &larr; Back to Long-Term Planning
          </Link>
        </div>
        <Alert variant="warning">
          <strong>{t('planning_results.scenario_not_found_title')}</strong>{' '}
          <Trans i18nKey="planning_results.scenario_not_found_text" components={[<Link to="/planning" />]} />
        </Alert>
      </Container>
    )
  }

  return (
    <Container style={{ maxWidth: '1400px' }}>
      {/* Page Header with Back Link */}
      <div className="mb-4">
        <Link
          to="/planning"
          style={{
            color: 'var(--color-neutral-600)',
            textDecoration: 'none',
            fontSize: 'var(--font-size-sm)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
        >
          &larr; {t('planning_results.back_link')}
        </Link>
        <h1
          style={{
            color: 'var(--color-primary-dark)',
            fontSize: 'var(--font-size-2xl)',
            marginTop: 'var(--space-3)',
            marginBottom: 'var(--space-2)',
          }}
        >
          {scenario?.name || t('planning_results.default_title')}
        </h1>
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-600)' }}>
          {t('planning_results.analysis_type_label')} {
            analysisType === 'optimization' ? t('planning_results.analysis_optimization') :
            analysisType === 'cba' ? t('planning_results.analysis_cba') :
            t('planning_results.analysis_long_term')
          }
          {currency && ` | ${t('planning_results.currency_label')} ${currency}`}
        </div>
      </div>

      {/* Conditional Results Rendering */}
      {analysisType === 'cba' && (
        <CBAResultsPage
          scenarioId={scenarioId}
          params={scenarioParams}
          communityId={communityId}
          currency={currency}
        />
      )}

      {analysisType === 'long_term' && (
        <LongTermResultsPage
          scenarioId={scenarioId}
          params={scenarioParams}
          communityId={communityId}
          community={community}
          currency={currency}
        />
      )}

      {analysisType === 'optimization' && (
        <OptimizationInlineResults
          scenarioId={scenarioId}
          community={community}
          scenario={scenario}
        />
      )}

      {!['cba', 'long_term', 'optimization'].includes(analysisType) && (
        <Alert variant="warning" className="mt-4">
          {t('planning_results.unknown_analysis', { type: analysisType })}
        </Alert>
      )}
    </Container>
  )
}

export default PlanningResultsPage
